import type { FC } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  ArchiveIcon,
  ArrowsClockwiseIcon,
  BellIcon,
  BriefcaseIcon,
  ChatIcon,
  CheckCircleIcon,
  CircleIcon,
  ClockCountdownIcon,
  FireIcon,
  HeartbeatIcon,
  RobotIcon,
  UserIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react'
import { formatDistanceToNow } from 'date-fns'

import { Notification, TaskPriorityEnum } from '@scrambl/shared'

import { PRIORITY_CONFIG } from '../tasks/shared/constants'

interface NotificationItemProps {
  notification: Notification
  onClick?: () => void
  onDismiss?: () => void
  onMarkAsRead?: () => void
  showActions?: boolean
  className?: string
}

export const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onClick,
  onDismiss,
  onMarkAsRead,
  showActions = true,
  className,
}) => {
  const isUnread = notification.status === 'UNREAD'

  const getNotificationIcon = () => {
    const iconClass = 'h-4 w-4'

    switch (notification.type) {
      case 'TASK_ASSIGNED':
      case 'TASK_STATUS_CHANGED':
      case 'TASK_DUE_SOON':
      case 'TASK_OVERDUE':
      case 'TASK_PRIORITY_CHANGED':
        return <CheckCircleIcon className={iconClass} />

      case 'COMMENT_MENTION':
      case 'NOTE_MENTION':
      case 'COMMENT_REPLY':
      case 'COMMENT_REACTION':
      case 'TASK_COMMENT_ADDED':
        return <ChatIcon className={iconClass} />

      case 'PROJECT_MEMBER_ADDED':
      case 'PROJECT_MEMBER_REMOVED':
      case 'ENTITY_MEMBER_ADDED':
        return <UserIcon className={iconClass} />

      case 'PROJECT_STATUS_CHANGED':
      case 'PROJECT_DUE_SOON':
        return <BriefcaseIcon className={iconClass} />

      case 'DEAL_ASSIGNED':
      case 'DEAL_STATUS_CHANGED':
      case 'LEAD_ASSIGNED':
      case 'LEAD_CONVERTED':
        return <ArchiveIcon className={iconClass} />

      case 'SUBSCRIPTION_EXPIRING':
      case 'SUBSCRIPTION_RENEWED':
      case 'SYSTEM_MAINTENANCE':
      case 'FEATURE_ANNOUNCEMENT':
        return <WarningCircleIcon className={iconClass} />

      case 'BURNOUT_RISK_TRANSITION':
        return <FireIcon className={cn(iconClass, 'text-amber-500')} />

      case 'ENTITY_HEALTH_AT_RISK':
        return <HeartbeatIcon className={cn(iconClass, 'text-red-500')} />

      case 'ENTITY_HEALTH_DECLINED':
        return <HeartbeatIcon className={cn(iconClass, 'text-amber-500')} />

      case 'ENTITY_HEALTH_IMPROVED':
        return <HeartbeatIcon className={cn(iconClass, 'text-emerald-500')} />

      case 'DEAL_HEALTH_AT_RISK':
        return <WarningIcon className={cn(iconClass, 'text-red-500')} />

      case 'LIFECYCLE_STAGE_AUTOMATED':
        return <ArrowsClockwiseIcon className={iconClass} />

      case 'AGENT_REVIEW_REQUESTED':
        return <RobotIcon className={cn(iconClass, 'text-amber-500')} weight="fill" />

      case 'TIMESHEET_ENTRY_SUBMITTED':
      case 'TIMESHEET_ENTRY_APPROVED':
      case 'TIMESHEET_ENTRY_REJECTED':
      case 'TIMESHEET_ENTRY_CHANGES_REQUESTED':
      case 'TIMESHEET_SUBMITTED':
      case 'TIMESHEET_APPROVED':
      case 'TIMESHEET_REJECTED':
      case 'TIMESHEET_CHANGES_REQUESTED':
      case 'TIMESHEET_CLOSE_REMINDER':
      case 'TIMESHEET_MISSING_REMINDER':
      case 'TIMESHEET_APPROVAL_DEADLINE':
        return <ClockCountdownIcon className={cn(iconClass, 'text-amber-600 dark:text-amber-400')} weight="fill" />

      default:
        return <BellIcon className={iconClass} />
    }
  }

  // Render priority indicator using the same design as TaskPriorityField
  const renderPriorityIndicator = (priority: string) => {
    const config =
      PRIORITY_CONFIG[priority as keyof typeof PRIORITY_CONFIG] ||
      PRIORITY_CONFIG[TaskPriorityEnum.MEDIUM]

    return (
      <div className="flex items-center gap-1">
        {config.isUrgent ? (
          <WarningIcon className="h-3 w-3 text-red-500" />
        ) : (
          <div className="flex items-center gap-[2px]">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'h-2.5 w-1 rounded-sm',
                  index < config.bars
                    ? config.color
                    : 'bg-gray-200 dark:bg-gray-700',
                )}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const getTimeAgo = () => {
    return formatDistanceToNow(new Date(notification.createdAt), {
      addSuffix: true,
    })
  }

  return (
    <div
      className={cn(
        'group relative flex cursor-pointer gap-3 p-4 transition-colors hover:bg-muted/50',
        isUnread && 'bg-muted/30',
        className,
      )}
      onClick={onClick}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2">
          <CircleIcon className="h-2 w-2 fill-primary text-primary" />
        </div>
      )}

      {/* Icon or Avatar */}
      <div className="mt-0.5 flex-shrink-0">
        {notification.actor ? (
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={notification.actor.image || undefined}
              alt={notification.actor.displayName || notification.actor.email}
            />
            <AvatarFallback>
              {(notification.actor.displayName || notification.actor.email)
                .charAt(0)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            {getNotificationIcon()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm', isUnread && 'font-medium')}>
          {notification.title}
        </p>
        {notification.message && (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
            {notification.message}
          </p>
        )}
        <div className="mt-1 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{getTimeAgo()}</span>
          {(notification.unreadMessageCount ?? 1) > 1 && (
            <span className="rounded-full border border-border/70 bg-muted/50 px-1.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
              ×{notification.unreadMessageCount}
            </span>
          )}
          {notification.priority && notification.priority !== 'NORMAL' && (
            <div className="flex items-center">
              {renderPriorityIndicator(notification.priority)}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {isUnread && onMarkAsRead && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={e => {
                    e.stopPropagation()
                    onMarkAsRead()
                  }}
                >
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark as read</TooltipContent>
            </Tooltip>
          )}
          {onDismiss && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={e => {
                    e.stopPropagation()
                    onDismiss()
                  }}
                >
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dismiss</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}
